import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  UserPlus, X, Users, Church, MessageCircle, RefreshCw, ChevronRight,
  Sparkles
} from "lucide-react";
import { getFriendSuggestions, dismissFriendSuggestion, sendFriendRequest, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

// Reason icon mapping
const reasonIcons = {
  same_parish: Church,
  mutual_friends: Users,
  same_diocese: Church,
  shared_groups: MessageCircle,
  recently_active: Sparkles
};

// Reason badge colors
const reasonColors = {
  same_parish: "bg-[var(--liturgical-primary)] text-white",
  mutual_friends: "bg-blue-500 text-white",
  same_diocese: "bg-purple-100 text-purple-800",
  shared_groups: "bg-green-100 text-green-800",
  recently_active: "bg-yellow-100 text-yellow-800"
};

function SuggestionCard({ suggestion, onAddFriend, onDismiss, compact = false }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  const handleAddFriend = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await onAddFriend(suggestion.user.user_id);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDismiss = async (e) => {
    e.stopPropagation();
    await onDismiss(suggestion.user.user_id);
  };
  
  const goToProfile = () => {
    navigate(`/profile/${suggestion.user.user_id}`);
  };
  
  // Get the primary reason (highest weight)
  const primaryReason = suggestion.reasons.find(r => r.weight === "high") || suggestion.reasons[0];
  const Icon = primaryReason ? reasonIcons[primaryReason.type] : Users;
  
  if (compact) {
    return (
      <div 
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F9F7F2] cursor-pointer transition-colors group"
        onClick={goToProfile}
        data-testid={`suggestion-${suggestion.user.user_id}`}
      >
        <Avatar className="w-10 h-10 border-2 border-[#E8E4DC]">
          <AvatarImage src={resolveImageUrl(suggestion.user.picture)} />
          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
            {suggestion.user.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <p className="font-['Nunito'] font-semibold text-sm text-[#2C2C2C] truncate">
            {suggestion.user.name}
          </p>
          {primaryReason && (
            <p className="text-xs text-[#6B665E] truncate flex items-center gap-1">
              <Icon className="w-3 h-3" />
              {primaryReason.text}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={handleDismiss}
            title="Dismiss"
          >
            <X className="w-4 h-4 text-[#6B665E]" />
          </Button>
          <Button
            size="sm"
            className="h-7 bg-[var(--liturgical-primary)] hover:brightness-90 text-white px-2"
            onClick={handleAddFriend}
            disabled={loading}
          >
            <UserPlus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }
  
  // Full card version for dedicated page
  return (
    <Card 
      className="bg-white border-[#E8E4DC] hover:shadow-md transition-shadow cursor-pointer"
      onClick={goToProfile}
      data-testid={`suggestion-card-${suggestion.user.user_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="w-14 h-14 border-2 border-[#E8E4DC]">
            <AvatarImage src={resolveImageUrl(suggestion.user.picture)} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-lg">
              {suggestion.user.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <h4 className="font-['Nunito'] font-semibold text-[#2C2C2C]">
              {suggestion.user.name}
            </h4>
            {suggestion.user.parish_name && (
              <p className="text-sm text-[#6B665E]">{suggestion.user.parish_name}</p>
            )}
            {suggestion.user.bio && (
              <p className="text-sm text-[#6B665E] mt-1 line-clamp-2">{suggestion.user.bio}</p>
            )}
            
            {/* Reasons - Why suggested */}
            <div className="flex flex-wrap gap-1 mt-2">
              {suggestion.reasons.slice(0, 3).map((reason, idx) => {
                const ReasonIcon = reasonIcons[reason.type] || Users;
                return (
                  <Badge 
                    key={idx} 
                    className={`${reasonColors[reason.type] || 'bg-gray-100 text-gray-800'} text-xs font-['Nunito'] gap-1`}
                  >
                    <ReasonIcon className="w-3 h-3" />
                    {reason.text}
                  </Badge>
                );
              })}
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
              onClick={handleAddFriend}
              disabled={loading}
            >
              <UserPlus className="w-4 h-4 mr-1" />
              {loading ? "..." : "Add"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-[#6B665E]"
              onClick={handleDismiss}
            >
              <X className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact widget for sidebar
export function FriendSuggestionsWidget({ limit = 5, showHeader = true, showViewAll = true }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadSuggestions();
  }, []);
  
  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const res = await getFriendSuggestions(limit);
      setSuggestions(res.data.suggestions);
    } catch {
      // Silently fail - widget is optional
    }
    setLoading(false);
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSuggestions();
    setRefreshing(false);
  };
  
  const handleAddFriend = async (userId) => {
    try {
      await sendFriendRequest(userId);
      toast.success("Friend request sent!");
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.user.user_id !== userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
  };
  
  const handleDismiss = async (userId) => {
    try {
      await dismissFriendSuggestion(userId);
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.user.user_id !== userId));
    } catch {
      // Silently fail
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-white/80 border-[#E8E4DC]">
        <CardContent className="py-6 flex justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-[var(--liturgical-primary)]" />
        </CardContent>
      </Card>
    );
  }
  
  if (suggestions.length === 0) {
    return null; // Don't show widget if no suggestions
  }
  
  return (
    <Card className="bg-white/80 border-[#E8E4DC]" data-testid="friend-suggestions-widget">
      {showHeader && (
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-['Cinzel'] text-[#2C2C2C] flex items-center gap-2">
            <Users className="w-4 h-4 text-[var(--liturgical-primary)]" />
            People You May Know
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-7 w-7 p-0"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
      )}
      
      <CardContent className={showHeader ? "pt-0" : "pt-4"}>
        <div className="space-y-1">
          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.user.user_id}
              suggestion={suggestion}
              onAddFriend={handleAddFriend}
              onDismiss={handleDismiss}
              compact={true}
            />
          ))}
        </div>
        
        {showViewAll && (
          <Button
            variant="ghost"
            className="w-full mt-2 text-[var(--liturgical-primary)] hover:text-white hover:bg-[var(--liturgical-primary)] font-['Nunito']"
            onClick={() => navigate("/discover-people")}
          >
            View All Suggestions
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Full page component
export function DiscoverPeoplePage() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  useEffect(() => {
    loadSuggestions();
  }, []);
  
  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const res = await getFriendSuggestions(20); // Load more for full page
      setSuggestions(res.data.suggestions);
    } catch {
      toast.error("Failed to load suggestions");
    }
    setLoading(false);
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSuggestions();
    setRefreshing(false);
  };
  
  const handleAddFriend = async (userId) => {
    try {
      await sendFriendRequest(userId);
      toast.success("Friend request sent!");
      setSuggestions(prev => prev.filter(s => s.user.user_id !== userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
  };
  
  const handleDismiss = async (userId) => {
    try {
      await dismissFriendSuggestion(userId);
      setSuggestions(prev => prev.filter(s => s.user.user_id !== userId));
    } catch {
      // Silently fail
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F9F7F2] pt-20 pb-8" data-testid="discover-people-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-['Cinzel'] font-bold text-[#2C2C2C] flex items-center gap-3">
              <Users className="w-7 h-7 text-[var(--liturgical-primary)]" />
              Discover People
            </h1>
            <p className="text-[#6B665E] font-['Nunito'] mt-1">
              Connect with others in your Catholic community
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="font-['Nunito']"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Suggestions Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
          </div>
        ) : suggestions.length === 0 ? (
          <Card className="bg-white border-[#E8E4DC]">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-[#6B665E] mx-auto mb-4" />
              <h3 className="text-lg font-['Cinzel'] font-semibold text-[#2C2C2C] mb-2">
                No Suggestions Right Now
              </h3>
              <p className="text-[#6B665E] font-['Nunito']">
                Check back later or expand your network by joining groups and being active in your parish.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {suggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.user.user_id}
                suggestion={suggestion}
                onAddFriend={handleAddFriend}
                onDismiss={handleDismiss}
                compact={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FriendSuggestionsWidget;
